import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";

/** Canonical SIP codecs offered on softphones / vendors / VICIdial. */
export const SIP_CODEC_OPTIONS = [
  { id: "G711", label: "G711", hint: "PCMU + PCMA" },
  { id: "G729", label: "G729", hint: "" },
  { id: "G723", label: "G723", hint: "" },
  { id: "GSM", label: "GSM", hint: "" },
];

export const DEFAULT_SIP_CODECS = ["G711"];

/** Normalize API / form values to a unique ordered list of known codec ids. */
export function normalizeSipCodecs(raw, { fallback = DEFAULT_SIP_CODECS } = {}) {
  const allowed = new Set(SIP_CODEC_OPTIONS.map((c) => c.id));
  const out = [];
  const seen = new Set();
  for (const item of raw || []) {
    const id = String(item || "").trim().toUpperCase().replace(/\./g, "");
    const mapped = id === "PCMU" || id === "PCMA" || id === "ULAW" || id === "ALAW" ? "G711" : id;
    if (allowed.has(mapped) && !seen.has(mapped)) {
      seen.add(mapped);
      out.push(mapped);
    }
  }
  return out.length ? out : [...fallback];
}

/**
 * Multi-select checkboxes for SIP codecs (one or all of G711 / G729 / G723 / GSM).
 */
export default function SipCodecPicker({
  value,
  onChange,
  label = "SIP codecs",
  testid = "sip-codecs",
  description = "Select one or more codecs. G711 maps to PCMU and PCMA.",
}) {
  const selected = normalizeSipCodecs(value);

  const toggle = (id, checked) => {
    let next;
    if (checked) {
      next = normalizeSipCodecs([...selected, id]);
    } else {
      next = selected.filter((c) => c !== id);
      if (next.length === 0) next = [id]; // keep at least one
    }
    onChange(next);
  };

  const selectAll = () => onChange(SIP_CODEC_OPTIONS.map((c) => c.id));

  return (
    <div className="space-y-1.5" data-testid={testid}>
      <div className="flex items-center justify-between gap-2">
        <Label>{label}</Label>
        <button
          type="button"
          className="text-[11px] text-muted-foreground hover:text-foreground underline-offset-2 hover:underline"
          onClick={selectAll}
          data-testid={`${testid}-all`}
        >
          Select all
        </button>
      </div>
      <div className="flex flex-wrap gap-x-4 gap-y-2 rounded-md border border-border px-3 py-2.5">
        {SIP_CODEC_OPTIONS.map((c) => {
          const on = selected.includes(c.id);
          return (
            <label
              key={c.id}
              className="inline-flex items-center gap-2 text-sm cursor-pointer select-none"
              data-testid={`${testid}-${c.id.toLowerCase()}`}
            >
              <Checkbox
                checked={on}
                onCheckedChange={(v) => toggle(c.id, !!v)}
              />
              <span>
                {c.label}
                {c.hint ? (
                  <span className="text-muted-foreground text-[11px] ml-1">({c.hint})</span>
                ) : null}
              </span>
            </label>
          );
        })}
      </div>
      {description ? (
        <p className="text-[11px] text-muted-foreground leading-snug">{description}</p>
      ) : null}
    </div>
  );
}
